// src/pages/BusinessSettings.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  TextField,
  Button,
  Avatar,
  Stack,
  Divider,
  InputAdornment,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Edit as EditIcon,
  FlightTakeoff as VacationIcon,
  Language as VisitIcon,
  InfoOutlined as InfoIcon,
  Save as SaveIcon,
} from "@mui/icons-material";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://printformee.com/api"; // change if you have backend

export default function InhouseShop() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery("(max-width:400px)");

  // --- component state
  const [saving, setSaving] = useState(false);

  // Shop-related state (simulate fetch)
  const [shop, setShop] = useState({
    id: "shop_001",
    name: "PrintForMee Shop",
    createdAt: "06 Dec, 2025",
    bannerUrl: "", // optional: base64 or url
    logoUrl: "", // optional
    tempClosed: false,
    minimumOrderAmount: 299,
    freeDeliveryOver: 299,
  });

  // When component mounts, fetch real shop data
  useEffect(() => {
    fetchShop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- optional: fetch shop from API
  async function fetchShop() {
    try {
      const res = await axios.get(`${API_BASE}/admin-settings`); // use correct endpoint
      if (res.data) {
        setShop((prev) => ({
          ...prev,
          name: res.data.businessName || "PrintForMee Shop",
          minimumOrderAmount: res.data.minimumOrderAmount || 299,
          freeDeliveryOver: res.data.freeDeliveryOver || 299,
          tempClosed: res.data.tempClosed || false,
        }));
      }
    } catch (err) {
      console.error("Failed to load shop settings:", err);
      // keep defaults
    }
  }

  // --- handlers
  const handleToggleTempClose = (e) => {
    setShop((s) => ({ ...s, tempClosed: e.target.checked }));
  };

  const handleFieldChange = (field) => (e) => {
    const value = e.target.value;
    // keep numbers as numbers when appropriate
    setShop((s) => ({
      ...s,
      [field]:
        field === "minimumOrderAmount" || field === "freeDeliveryOver"
          ? value === ""
            ? ""
            : Number(value)
          : value,
    }));
  };

  const handleSave = async () => {
    // basic validation
    if (
      shop.minimumOrderAmount === "" ||
      isNaN(Number(shop.minimumOrderAmount))
    ) {
      alert("Please enter a valid Minimum Order Amount.");
      return;
    }
    if (shop.freeDeliveryOver === "" || isNaN(Number(shop.freeDeliveryOver))) {
      alert("Please enter a valid Free Delivery Over Amount.");
      return;
    }

    setSaving(true);
    try {
      // Call API to save shop settings
      const shopData = {
        businessName: shop.name,
        minimumOrderAmount: shop.minimumOrderAmount,
        freeDeliveryOver: shop.freeDeliveryOver,
        tempClosed: shop.tempClosed,
      };

      const res = await axios.put(`${API_BASE}/admin-settings`, shopData);

      setSaving(false);
      alert("Shop settings saved successfully!");
      console.log("Shop settings saved:", res.data);
    } catch (err) {
      console.error("Save failed:", err);
      setSaving(false);
      alert("Failed to save. See console for details.");
    }
  };

  const handleEditShop = () => {
    // navigate to edit page or show a dialog
    alert("Open Edit Shop page (implement navigation).");
  };

  const handleVacationMode = () => {
    alert("Go to Vacation Mode settings (implement navigation).");
  };

  const handleVisitWebsite = () => {
    // open public shop in new tab
    const publicUrl = `/shop/${shop.id}`; // change to real public url if available
    window.open(publicUrl, "_blank", "noopener,noreferrer");
  };

  // --- small helper for formatted currency input (just display)
  const formatCurrency = (v) => {
    if (v === "" || v === null || v === undefined) return "";
    return Number(v).toString();
  };

  // --- UI rendering
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 3 },
        maxWidth: "100vw",
        overflowX: "hidden",
        width: "100%",
      }}
    >
      <Typography
        variant={isMobile ? "h6" : "h5"}
        sx={{
          mb: { xs: 2, sm: 3 },
          fontWeight: 600,
          fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
        }}
      >
        Business Setup
      </Typography>

      {/* Temporary Close Card */}
      <Card
        variant="outlined"
        sx={{
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: 1, sm: 2 },
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={10}>
              <Typography
                variant={isMobile ? "subtitle2" : "subtitle1"}
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  lineHeight: { xs: 1.4, sm: 1.5 },
                }}
              >
                Temporary close
              </Typography>
              <Typography
                variant={isMobile ? "caption" : "body2"}
                color="text.secondary"
                sx={{
                  mt: 1,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  lineHeight: { xs: 1.4, sm: 1.5 },
                  wordBreak: "break-word",
                  maxWidth: "100%",
                }}
              >
                *By turning on the "Temporary Close" Button admin can pause his
                shop activities and his shop will be shown as "Temporary Close"
                in the system . Customers will not be able to order or purchase
                from his shop
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              md={2}
              sx={{
                textAlign: { xs: "left", md: "right" },
                mt: { xs: 2, md: 0 },
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                alignItems: "center",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={!!shop.tempClosed}
                    onChange={handleToggleTempClose}
                    color="primary"
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ "aria-label": "temporary close toggle" }}
                  />
                }
                label={shop.tempClosed ? "Closed" : "Open"}
                labelPlacement="start"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Shop Details Card */}
      <Card
        variant="outlined"
        sx={{
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: 1, sm: 2 },
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                  lineHeight: { xs: 1.3, sm: 1.4 },
                }}
              >
                Shop Details
              </Typography>
              <Typography
                variant={isMobile ? "caption" : "body2"}
                color="text.secondary"
                sx={{
                  mb: 2,
                  fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.875rem" },
                  lineHeight: { xs: 1.3, sm: 1.4 },
                }}
              >
                Created at {shop.createdAt}
              </Typography>
            </Grid>

            {/* Banner */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  background:
                    "linear-gradient(135deg,#0f3b73 0%, #082b57 40%, rgba(10,30,60,0.9) 60%)",
                  position: "relative",
                  minHeight: { xs: 170, sm: 150, md: 120 },
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "center", sm: "center" },
                  justifyContent: "space-between",
                  p: { xs: 2, sm: 3 },
                  gap: { xs: 2, sm: 0 },
                }}
              >
                {/* LEFT SIDE */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    gap: 2,
                    textAlign: { xs: "center", sm: "left" },
                  }}
                >
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: { xs: 60, sm: 80 },
                      height: { xs: 40, sm: 50 },
                      bgcolor: "#fff",
                    }}
                  >
                    P
                  </Avatar>

                  <Box>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: { xs: "0.9rem", sm: "1.1rem" },
                      }}
                    >
                      {shop.name}
                    </Typography>

                    <Button
                      variant="text"
                      startIcon={<VisitIcon />}
                      onClick={handleVisitWebsite}
                      sx={{
                        color: "#fff",
                        fontSize: "0.75rem",
                        mt: 0.5,
                      }}
                    >
                      Visit website
                    </Button>
                  </Box>
                </Box>

                {/* RIGHT SIDE BUTTONS */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<VacationIcon />}
                    onClick={handleVacationMode}
                    sx={{
                      borderColor: "rgba(255,255,255,0.3)",
                      color: "#fff",
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    Go to Vacation Mode
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEditShop}
                    sx={{
                      backgroundColor: "#0b66b3",
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    Edit shop
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Shop Settings */}
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: { xs: 1, sm: 2 },
                  mt: { xs: 2, sm: 0 },
                  width: "100%",
                  maxWidth: "100%",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant={isExtraSmall ? "subtitle2" : "subtitle1"}
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.8rem", sm: "1rem" },
                          }}
                        >
                          Shop Settings
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size={
                          isExtraSmall ? "small" : isMobile ? "small" : "medium"
                        }
                        label={
                          <span>
                            Minimum Order Amount &nbsp;
                            <Tooltip title="Minimum order price required to place an order">
                              <InfoIcon
                                sx={{ fontSize: 16, verticalAlign: "middle" }}
                              />
                            </Tooltip>
                          </span>
                        }
                        value={formatCurrency(shop.minimumOrderAmount)}
                        onChange={handleFieldChange("minimumOrderAmount")}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                          inputMode: "numeric",
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size={
                          isExtraSmall ? "small" : isMobile ? "small" : "medium"
                        }
                        label="Free Delivery Over Amount (₹)"
                        value={formatCurrency(shop.freeDeliveryOver)}
                        onChange={handleFieldChange("freeDeliveryOver")}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                          inputMode: "numeric",
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Save area */}
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-end" },
          gap: 2,
          mt: { xs: 2, sm: 3 },
          px: { xs: 1, sm: 0 },
        }}
      >
        <Divider
          sx={{
            flex: "1 1 auto",
            display: { xs: "none", sm: "block" },
          }}
        />
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size={isMobile ? "medium" : "large"}
          fullWidth={isMobile}
          sx={{
            minWidth: { xs: "100%", sm: 160 },
            py: { xs: 1.5, sm: 1 },
            fontSize: { xs: "0.875rem", sm: "1rem" },
          }}
        >
          {saving ? "Saving..." : "Save information"}
        </Button>
      </Box>
    </Box>
  );
}

// small util used inside component
function formatCurrencyUtil(v) {
  if (v === "" || v === undefined || v === null) return "";
  return Number(v).toString();
}
